import { useState, useEffect } from 'react';
import api from '../../api/axios';

const ACTIVITY_TYPES = [
  { id: 'follow_up', label: 'Follow-up' },
  { id: 'call', label: 'Call' },
  { id: 'meeting', label: 'Meeting' },
  { id: 'site_visit', label: 'Site Visit' },
  { id: 'note', label: 'Note' },
  { id: 'email', label: 'Email' },
  { id: 'other', label: 'Other' },
];

const pad = (n) => String(n).padStart(2, '0');

function toDatetimeLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DashboardFollowUpModal({ isOpen, onClose, onSuccess, salespersons = [] }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [form, setForm] = useState({
    type: 'follow_up',
    title: '',
    description: '',
    followUpDate: '',
    cc: [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Set default follow-up date to tomorrow 10:00 AM on open
  useEffect(() => {
    if (isOpen) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      setForm((prev) => ({
        ...prev,
        followUpDate: toDatetimeLocal(tomorrow),
      }));

      setLoadingProjects(true);
      api.get('/projects', { params: { limit: 100 } })
        .then((res) => {
          const list = res.data?.data?.projects || res.data?.projects || [];
          setProjects(list);
        })
        .catch(console.error)
        .finally(() => setLoadingProjects(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredProjects = projects.filter((p) => {
    if (!searchFilter.trim()) return true;
    const term = searchFilter.toLowerCase();
    const name = (p.client?.name || p.clientName || '').toLowerCase();
    const phone = (p.client?.phone || p.phone || '').toLowerCase();
    return name.includes(term) || phone.includes(term);
  });

  const setPresetDate = (hoursFromNow, targetHour = null) => {
    const d = new Date();
    if (targetHour !== null) {
      d.setDate(d.getDate() + (hoursFromNow / 24));
      d.setHours(targetHour, 0, 0, 0);
    } else {
      d.setTime(d.getTime() + hoursFromNow * 60 * 60 * 1000);
    }
    setForm((prev) => ({ ...prev, followUpDate: toDatetimeLocal(d) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCcChange = (userId) => {
    setForm((prev) => {
      const exists = prev.cc.includes(userId);
      return {
        ...prev,
        cc: exists ? prev.cc.filter((id) => id !== userId) : [...prev.cc, userId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim() && !form.description.trim()) {
      setError('Please provide a title or description for the follow-up.');
      return;
    }

    const selectedProj = projects.find((p) => p._id === selectedProjectId);
    const clientId = typeof selectedProj?.client === 'object'
      ? selectedProj.client?._id
      : selectedProj?.client;

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim() || form.description.slice(0, 60) || 'Follow-up',
        description: form.description,
        type: form.type,
        followUpDate: form.followUpDate || null,
        client: clientId || undefined,
        project: selectedProjectId || undefined,
        cc: form.cc,
      };

      await api.post('/activity-logs', payload);
      onSuccess?.('Follow-up scheduled successfully!');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to schedule follow-up.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal dash-action-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">📞 Add Follow-up</h3>
            <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}>
              Log a call, schedule a client follow-up, or record site notes
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.65rem 0.9rem', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            {/* Select Client / Lead / Project */}
            <div className="form-group">
              <label className="form-label">Link to Client / Lead (Optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="Type to filter client by name or phone..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ marginBottom: 6, fontSize: '0.82rem', padding: '6px 10px' }}
              />
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={loadingProjects}
              >
                <option value="">General / General Office Task (Not linked)</option>
                {filteredProjects.map((p) => {
                  const name = p.client?.name || p.clientName || 'Client';
                  const phone = p.client?.phone || p.phone || '';
                  const stage = p.stage || '';
                  return (
                    <option key={p._id} value={p._id}>
                      {name} {phone ? `(${phone})` : ''} · {stage}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Interaction Type *</label>
                <select
                  name="type"
                  className="form-select"
                  value={form.type}
                  onChange={handleChange}
                  required
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Follow-up Due Date & Time</label>
                <input
                  type="datetime-local"
                  name="followUpDate"
                  className="form-input"
                  value={form.followUpDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Quick date preset buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '1.1rem', marginTop: '-0.4rem' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--muted)', alignSelf: 'center', marginRight: '4px' }}>
                Quick Set:
              </span>
              <button
                type="button"
                className="dash-pill-btn"
                style={{ padding: '3px 9px', fontSize: '0.74rem', background: '#fff', border: '1px solid #E2E8F0' }}
                onClick={() => setPresetDate(2)}
              >
                +2 Hours
              </button>
              <button
                type="button"
                className="dash-pill-btn"
                style={{ padding: '3px 9px', fontSize: '0.74rem', background: '#fff', border: '1px solid #E2E8F0' }}
                onClick={() => setPresetDate(24, 10)}
              >
                Tomorrow 10 AM
              </button>
              <button
                type="button"
                className="dash-pill-btn"
                style={{ padding: '3px 9px', fontSize: '0.74rem', background: '#fff', border: '1px solid #E2E8F0' }}
                onClick={() => setPresetDate(72, 11)}
              >
                In 3 Days
              </button>
              <button
                type="button"
                className="dash-pill-btn"
                style={{ padding: '3px 9px', fontSize: '0.74rem', background: '#fff', border: '1px solid #E2E8F0' }}
                onClick={() => setPresetDate(168, 11)}
              >
                Next Week
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Follow-up Summary / Title *</label>
              <input
                type="text"
                name="title"
                className="form-input"
                placeholder="e.g. Call client for site visit or quote review"
                value={form.title}
                onChange={handleChange}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Discussion Notes / Instructions</label>
              <textarea
                name="description"
                className="form-textarea"
                placeholder="Enter client response, key requirements, or discussion points..."
                value={form.description}
                onChange={handleChange}
                rows="3"
              />
            </div>

            {/* CC / Notify Team Members */}
            {salespersons.length > 0 && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Share with Team Members (CC)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: 80, overflowY: 'auto', padding: '4px 0' }}>
                  {salespersons.map((s) => {
                    const isSelected = form.cc.includes(s._id);
                    return (
                      <button
                        key={s._id}
                        type="button"
                        onClick={() => handleCcChange(s._id)}
                        className={`dash-pill-btn ${isSelected ? 'active' : ''}`}
                        style={{
                          fontSize: '0.74rem',
                          padding: '3px 10px',
                          border: `1px solid ${isSelected ? '#976793' : '#E2E8F0'}`,
                          background: isSelected ? '#976793' : '#fff',
                          color: isSelected ? '#fff' : 'var(--charcoal)',
                        }}
                      >
                        {isSelected ? '✓ ' : '+ '} {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ background: '#976793' }}
            >
              {saving ? 'Scheduling...' : 'Save Follow-up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
